import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, User, Briefcase, ShieldCheck } from "lucide-react";
import { LOGO, IMAGES } from "@/lib/constants";
import { Seo } from "@/components/Seo";
import { useLang } from "@/context/LanguageContext";

export default function Espaces() {
  const { t } = useLang();
  const navigate = useNavigate();

  const spaces = [
    { key: "client", icon: User, to: "/connexion?espace=client" },
    { key: "staff", icon: Briefcase, to: "/connexion?espace=staff" },
    { key: "admin", icon: ShieldCheck, to: "/connexion?espace=admin" },
  ];

  return (
    <div data-testid="espaces-page" className="relative min-h-screen bg-navy overflow-hidden flex flex-col">
      <Seo title="Choisir mon espace | Work & Travel Expert" description="Sélectionnez votre espace pour vous connecter : espace client, collaborateur ou administrateur Work & Travel Expert." path="/acces" noindex />
      <img src={IMAGES.miami} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover opacity-20" />
      <div className="absolute inset-0 bg-gradient-to-b from-navy/80 via-navy to-navy" />
      <div className="absolute -top-32 -right-24 w-96 h-96 rounded-full bg-gold/10 blur-3xl" />

      <div className="relative z-10 flex-1 flex flex-col justify-center max-w-6xl mx-auto w-full px-5 sm:px-8 py-16">
        <Link to="/" data-testid="espaces-back" className="flex items-center gap-2 text-sm text-white/70 hover:text-gold transition-colors mb-10 self-start">
          <ArrowLeft size={16} /> {t("select.back")}
        </Link>

        <div className="flex items-center gap-4">
          <div className="bg-white rounded-full h-16 w-16 flex items-center justify-center p-1 shrink-0">
            <img src={LOGO} alt="Work & Travel Expert" className="h-full w-full object-contain rounded-full" />
          </div>
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">{t("select.title")}</h1>
            <p className="text-white/70 mt-2 text-sm sm:text-base">{t("select.subtitle")}</p>
          </div>
        </div>
        <div className="gold-line mt-6" />

        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {spaces.map((s, i) => (
            <motion.button
              key={s.key}
              data-testid={`space-${s.key}`}
              onClick={() => navigate(s.to)}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 + i * 0.1 }}
              className="group text-left rounded-2xl border border-white/12 bg-white/[0.04] backdrop-blur-sm p-7 hover:border-gold hover:bg-white/[0.07] transition-all duration-300"
            >
              <div className="h-14 w-14 rounded-xl border border-gold/40 bg-gold/10 flex items-center justify-center text-gold group-hover:bg-gold group-hover:text-white transition-colors">
                <s.icon size={26} />
              </div>
              <h2 className="font-heading text-2xl text-white mt-6">{t(`select.${s.key}`)}</h2>
              <p className="text-white/65 text-sm mt-2 leading-relaxed">{t(`select.${s.key}Desc`)}</p>
              <span className="inline-flex items-center gap-2 mt-6 text-gold font-medium text-sm">
                {t("select.cta")} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </span>
            </motion.button>
          ))}
        </div>

        <p className="text-white/50 text-xs mt-10 max-w-2xl">{t("select.note")}</p>
      </div>
    </div>
  );
}
